import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Scale, CheckCircle2, XCircle, AlertTriangle, Loader2, Mail } from "lucide-react";
import FinalisationPanel from "@/pages/dashboard/fieldclaim/FinalisationPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const Icon = ({ severity }) =>
  severity === "pass" ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
    : severity === "fail" ? <XCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
      : <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />;
const BADGE = {
  pending: "border-white/20 text-white/50", approved: "border-emerald-400/50 text-emerald-300",
  reduced: "border-amber-400/50 text-amber-300", rejected: "border-red-400/50 text-red-300",
};

export default function AssessorDemo() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/assessor-demo/${shareId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "This demo link is not valid."));
  }, [shareId]);

  const totals = () => {
    let claimed = 0, approved = 0;
    (data?.assets || []).forEach((a) => {
      const c = a.claimed || 0;
      claimed += c;
      approved += a.approved_amount != null ? a.approved_amount : (a.decision === "rejected" ? 0 : c);
    });
    const inel = Math.max(claimed - approved, 0);
    return { claimed, approved, inel, pct: claimed ? (inel / claimed) * 100 : 0 };
  };
  const t = totals();

  return (
    <div className="min-h-screen bg-navy-950 text-white" data-testid="assessor-demo-page">
      <header className="border-b border-gold-500/20 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-gold-500 text-navy-950 flex items-center justify-center font-heading font-bold">MD</div>
          <div>
            <Mono className="text-[0.55rem] text-gold-400">Managing Disaster Australia</Mono>
            <p className="font-heading text-lg leading-tight">Assessor mode — live demonstration</p>
          </div>
        </div>
        <Mono className="text-[0.5rem] text-white/40">Read-only · no login required · demonstration data</Mono>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        {err && <p className="text-amber-300" data-testid="demo-error">{err}</p>}
        {!data && !err && <div className="flex items-center gap-2 text-white/50"><Loader2 className="h-4 w-4 animate-spin" /> Loading the demonstration claim…</div>}

        {data && (
          <>
            <div>
              <Mono className="text-gold-400 text-[0.6rem]">{data.claim.assessing_body} · SmartyGrants · {data.claim.event_date}</Mono>
              <h1 className="font-heading font-light text-4xl tracking-tight mt-2" data-testid="demo-claim-title">
                {`AGRN${(data.claim.agrn || "").replace(/agrn[-\s]*/i, "")} — ${data.claim.event_name}`}
              </h1>
              <p className="text-white/50 font-light mt-3 max-w-3xl text-sm">
                This claim has been seeded with the compliance failures assessors actually see — a pre-event invoice, an
                out-of-window work order, on-costs above cap, indirect costs mixed into direct works, and an unjustified
                benchmark exceedance. Everything flagged below was caught automatically, before a human opened the claim.
              </p>
            </div>

            <div className="border border-gold-500/20 bg-navy-900/40 p-5 space-y-3" data-testid="demo-checks">
              <Mono className="text-[0.55rem] text-gold-400 flex items-center gap-2"><Scale className="h-3.5 w-3.5" /> Assessment intelligence — automated checks</Mono>
              {data.checks.map((c) => (
                <div key={c.code} className="flex items-start gap-2.5" data-testid={`demo-check-${c.code}`}>
                  <Icon severity={c.severity} />
                  <div>
                    <span className="text-sm">{c.label}{c.amount ? <span className="font-mono text-xs text-gold-300 ml-2">${Number(c.amount).toLocaleString()}</span> : null}</span>
                    <p className="text-white/45 font-light text-xs mt-0.5">{c.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="border border-gold-500/20 bg-navy-900/40 p-5 overflow-x-auto">
              <Mono className="text-[0.55rem] text-gold-400">Line determinations — recorded by {data.assessor || "the assessor"}</Mono>
              <table className="w-full text-sm min-w-[860px] mt-3" data-testid="demo-lines">
                <thead><tr className="border-b border-gold-500/20 text-left">
                  {["Asset", "Measure", "Claimed", "Benchmark", "Var", "Decision", "Approved", "Assessment reason"].map((h) => (
                    <th key={h} className="py-2 pr-3"><Mono className="text-[0.5rem] text-white/40">{h}</Mono></th>
                  ))}
                </tr></thead>
                <tbody>
                  {data.assets.map((a) => (
                    <tr key={a.id} className="border-b border-white/5 align-top" data-testid={`demo-line-${a.site_number}`}>
                      <td className="py-2.5 pr-3"><span className="text-white">{a.name}</span><span className="text-white/35 text-xs block">{a.site_number}</span></td>
                      <td className="py-2.5 pr-3 font-mono text-xs text-gold-300">{a.funding_measure}</td>
                      <td className="py-2.5 pr-3 font-mono text-xs">{money(a.claimed)}</td>
                      <td className="py-2.5 pr-3 font-mono text-xs text-white/50">{money(a.cet_cost)}</td>
                      <td className={`py-2.5 pr-3 font-mono text-xs ${a.variance_pct > 20 ? "text-amber-300" : "text-white/50"}`}>{a.variance_pct != null ? `${a.variance_pct}%` : "—"}</td>
                      <td className="py-2.5 pr-3"><Mono className={`text-[0.5rem] border px-1.5 py-0.5 ${BADGE[a.decision]}`}>{a.decision}</Mono></td>
                      <td className="py-2.5 pr-3 font-mono text-xs text-emerald-300/90">{money(a.approved_amount)}</td>
                      <td className="py-2.5 text-white/55 font-light text-xs max-w-xs">{a.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex flex-wrap gap-8 mt-5 pt-4 border-t border-white/10 text-sm" data-testid="demo-summary">
                <span>Claimed <span className="font-mono text-white block">{money(t.claimed)}</span></span>
                <span>Approved <span className="font-mono text-emerald-300 block">{money(t.approved)}</span></span>
                <span>Ineligible <span className="font-mono text-amber-300 block">{money(t.inel)}</span></span>
                <span>Payment-release tolerance
                  <span className={`font-mono block ${t.pct <= 5 ? "text-emerald-300" : "text-red-300"}`} data-testid="demo-tolerance">
                    {t.pct.toFixed(2)}% {t.pct <= 5 ? "· within 5% — proceed" : "· exceeds 5% — escalate"}
                  </span>
                </span>
              </div>
            </div>

            {data.finalisation?.has_ledger && (
              <div className="space-y-3" data-testid="demo-finalisation">
                <div>
                  <Mono className="text-[0.55rem] text-gold-400">Ledger finalisation — EW / IRW / EPAR</Mono>
                  <p className="text-white/50 font-light text-sm mt-1 max-w-3xl">
                    Finalisation is where claims are won and lost: every ledger line ruled eligible or ineligible against its
                    measure's completion window, duplicates caught, and on-costs computed from the ledger's actual labour base —
                    not a separate salary spreadsheet.
                  </p>
                </div>
                <FinalisationPanel data={data.finalisation} readOnly />
              </div>
            )}

            <div className="border border-gold-500/40 bg-gold-500/5 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="demo-cta">
              <div>
                <p className="font-heading text-xl">The determination export generates the moment the assessment is done.</p>
                <p className="text-white/55 font-light text-sm mt-1">No portal windows. No SharePoint links. One evidence chain for the council preparing the claim and the assessor approving it.</p>
              </div>
              <a href="mailto:hello@cbkm.au?subject=Assessor%20mode%20walkthrough" data-testid="demo-contact"
                className="inline-flex items-center gap-2 bg-gold-500 text-navy-950 px-5 py-3 text-sm font-medium hover:bg-gold-400">
                <Mail className="h-4 w-4" /> Book a 30-minute walkthrough
              </a>
            </div>
          </>
        )}
      </main>
      <footer className="border-t border-white/10 px-6 py-4">
        <Mono className="text-[0.5rem] text-white/30">Managing Disaster Australia · CBKM.au · hello@cbkm.au · demonstration data only — no council information is shown</Mono>
      </footer>
    </div>
  );
}
